import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Grid,
  Menu,
  ListItemIcon,
  CircularProgress,
  Autocomplete,
  TextField,
  MenuItem,
} from '@mui/material';
import { 
  Business, 
  TrendingUp, 
  CheckCircle, 
  Schedule,
  Download,
  PictureAsPdf,
  Description,
  Search,
} from '@mui/icons-material';
import { getProjectReport } from '../../services/reports';
import { getProjectsPaginated } from '../../services/project';

const ProjectReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Date filter states
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Project dropdown states with pagination
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectSearchInput, setProjectSearchInput] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const projectFetchingRef = useRef(false);
  const projectInitialLoadRef = useRef(false);
  const [projectPage, setProjectPage] = useState(1);
  const [projectHasMore, setProjectHasMore] = useState(true);
  const projectSearchTermRef = useRef('');
  
  // Project report data states
  const [projectReportData, setProjectReportData] = useState([]);
  const [loadingProjectReport, setLoadingProjectReport] = useState(false);
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Helper function to format currency amounts with proper Indian number formatting
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0';
    return Number(amount).toLocaleString('en-IN', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0,
      useGrouping: true
    });
  };

  // Helper function to format regular numbers with Indian formatting
  const formatNumber = (number) => {
    if (number === null || number === undefined) return '0';
    return Number(number).toLocaleString('en-IN');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Fetch data for the new page
    fetchProjectReportData(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Fetch data for the first page with new rows per page
    fetchProjectReportData(1, newRowsPerPage);
  };

  // Fetch projects for dropdown with pagination
  const fetchProjects = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (projectFetchingRef.current) return;
    if (!append && !projectHasMore && pageNum > 1) return;
    
    projectFetchingRef.current = true;
    setLoadingProjects(true);
    try {
      const params = { page: pageNum, limit: 20 };
      if (searchTerm && searchTerm.trim().length > 0) {
        params.search = searchTerm.trim();
      }
      const response = await getProjectsPaginated(params);
      
      let projects = [];
      let total = 0;
      if (response.status && Array.isArray(response.data)) {
        projects = response.data;
        total = parseInt(response.extra?.total || response.data?.totalDocs || response.count || projects.length, 10);
      }
      
      const formattedProjects = projects.map((project) => ({
        id: project._id || project.id,
        name: project.projectName || '',
        projectNo: project.projectNo || '',
        projectType: project.projectType || '',
      }));

      if (append) {
        setProjectOptions(prev => {
          const newOptions = [...prev, ...formattedProjects];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedProjects.length >= params.limit;
          setProjectHasMore(hasMore);
          return newOptions;
        });
      } else {
        setProjectOptions(formattedProjects);
        const currentTotal = formattedProjects.length;
        const hasMore = currentTotal < total && formattedProjects.length >= params.limit;
        setProjectHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      if (!append) {
        setProjectOptions([]);
      }
    } finally {
      setLoadingProjects(false);
      projectFetchingRef.current = false;
    }
  }, [projectHasMore]);

  // Debounced search for projects
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (projectSearchInput.length >= 2 || projectSearchInput.length === 0) {
        if (projectSearchTermRef.current !== projectSearchInput) {
          setProjectPage(1);
          setProjectHasMore(true);
          projectSearchTermRef.current = projectSearchInput;
          fetchProjects(projectSearchInput, 1, false);
        }
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [projectSearchInput, fetchProjects]);

  // Fetch project report data
  const fetchProjectReportData = useCallback(async (pageNum = 1, limit = 10) => {
    setLoadingProjectReport(true);
    try {
      const params = {
        page: pageNum,
        limit: limit,
      };

      // Add project filter if selected
      if (selectedProject && selectedProject.id) {
        params.projectId = selectedProject.id;
      }

      // Add date filters if provided
      if (fromDate) {
        params.fromDate = fromDate;
      }
      if (toDate) {
        params.toDate = toDate;
      }

      const response = await getProjectReport(params);

      if (response.success && Array.isArray(response.data)) {
        // Map API response to table format
        const mappedData = response.data.map((item, index) => ({
          sno: (pageNum - 1) * limit + index + 1,
          projectId: item.projectId || '',
          projectNo: item.projectNo || '',
          projectName: item.projectName || '',
          projectType: item.projectType || '',
          totalInventory: item.totalInventory || 0,
          bookedInventory: item.bookedInventory || 0,
          availableInventory: item.availableInventory || 0,
          totalBookings: item.totalBookings || 0,
          totalBookingAmount: item.totalBookingAmount || 0,
          totalPaidAmount: item.totalPaidAmount || 0,
          totalDueAmount: item.totalDueAmount || 0,
          cancelledBookings: item.cancelledBookings || 0,
          _raw: item, // Store raw data
        }));

        setProjectReportData(mappedData);
        setTotalProjects(response.pagination?.totalProjects || mappedData.length);
        setTotalPages(response.pagination?.totalPages || 1);
      } else {
        setProjectReportData([]);
        setTotalProjects(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching project report:', error);
      setProjectReportData([]);
      setTotalProjects(0);
      setTotalPages(0);
    } finally {
      setLoadingProjectReport(false);
    }
  }, [selectedProject, fromDate, toDate]);

  // Fetch data on initial load
  useEffect(() => {
    fetchProjectReportData(page + 1, rowsPerPage);
  }, []); // Only on mount

  // Export functionality
  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExport = (type) => {
    if (type === 'csv') {
      handleExportCsv();
    } else if (type === 'pdf') {
      handleExportPdf();
    }
    handleExportClose();
  };

  const handleExportCsv = () => {
    const headers = ["S.No", "Project No", "Project Name", "Project Type", "Total Inventory", "Booked", "Available", "Total Bookings", "Booking Amount", "Paid Amount", "Due Amount", "Cancelled"];
    const rows = projectReportData.map(project => [
      project.sno,
      project.projectNo,
      project.projectName,
      project.projectType,
      project.totalInventory,
      project.bookedInventory,
      project.availableInventory,
      project.totalBookings,
      project.totalBookingAmount,
      project.totalPaidAmount,
      project.totalDueAmount,
      project.cancelledBookings
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Project_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPdf = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write('<html><head><title>Project Report</title>');
    printWindow.document.write('<style>');
    printWindow.document.write(`
      body { font-family: Arial, sans-serif; margin: 20px; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
      th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
      th { background-color: #f2f2f2; font-weight: bold; }
      .header { text-align: center; margin-bottom: 20px; }
      .summary { margin-bottom: 20px; }
      .summary-item { display: inline-block; margin-right: 20px; padding: 10px; background-color: #f9f9f9; border-radius: 5px; }
    `);
    printWindow.document.write('</style></head><body>');
    printWindow.document.write('<div class="header"><h1>Project Report</h1>');
    printWindow.document.write(`<p>Generated on: ${new Date().toLocaleDateString()}</p></div>`);
    
    // Summary
    const totalProjects = projectReportData.length;
    const totalBookingAmount = projectReportData.reduce((sum, p) => sum + (p.totalBookingAmount || 0), 0);
    const totalPaidAmount = projectReportData.reduce((sum, p) => sum + (p.totalPaidAmount || 0), 0);
    const totalDueAmount = projectReportData.reduce((sum, p) => sum + (p.totalDueAmount || 0), 0);
    
    printWindow.document.write('<div class="summary">');
    printWindow.document.write(`<div class="summary-item">Total Projects: ${totalProjects}</div>`);
    printWindow.document.write(`<div class="summary-item">Total Booking Amount: Rs. ${formatCurrency(totalBookingAmount)}</div>`);
    printWindow.document.write(`<div class="summary-item">Total Paid Amount: Rs. ${formatCurrency(totalPaidAmount)}</div>`);
    printWindow.document.write(`<div class="summary-item">Total Due Amount: Rs. ${formatCurrency(totalDueAmount)}</div>`);
    printWindow.document.write('</div>');
    
    // Table
    printWindow.document.write('<table>');
    printWindow.document.write('<thead><tr>');
    const headers = ["S.No", "Project No", "Project Name", "Project Type", "Total Inventory", "Booked", "Available", "Total Bookings", "Booking Amount", "Paid Amount", "Due Amount", "Cancelled"];
    headers.forEach(header => {
      printWindow.document.write(`<th>${header}</th>`);
    });
    printWindow.document.write('</tr></thead><tbody>');
    
    projectReportData.forEach((project, index) => {
      printWindow.document.write('<tr>');
      printWindow.document.write(`<td>${index + 1}</td>`);
      printWindow.document.write(`<td>${project.projectNo}</td>`);
      printWindow.document.write(`<td>${project.projectName}</td>`);
      printWindow.document.write(`<td>${project.projectType}</td>`);
      printWindow.document.write(`<td>${project.totalInventory}</td>`);
      printWindow.document.write(`<td>${project.bookedInventory}</td>`);
      printWindow.document.write(`<td>${project.availableInventory}</td>`);
      printWindow.document.write(`<td>${project.totalBookings}</td>`);
      printWindow.document.write(`<td>Rs. ${formatCurrency(project.totalBookingAmount)}</td>`);
      printWindow.document.write(`<td>Rs. ${formatCurrency(project.totalPaidAmount)}</td>`);
      printWindow.document.write(`<td>Rs. ${formatCurrency(project.totalDueAmount)}</td>`);
      printWindow.document.write(`<td>${project.cancelledBookings}</td>`);
      printWindow.document.write('</tr>');
    });
    
    printWindow.document.write('</tbody></table></body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  // Calculate totals
  const totalBookingAmount = projectReportData.reduce((sum, p) => sum + (p.totalBookingAmount || 0), 0);
  const totalPaidAmount = projectReportData.reduce((sum, p) => sum + (p.totalPaidAmount || 0), 0);
  const totalDueAmount = projectReportData.reduce((sum, p) => sum + (p.totalDueAmount || 0), 0);
  const totalBookings = projectReportData.reduce((sum, p) => sum + (p.totalBookings || 0), 0);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Project Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive project management and tracking report
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<Download />}
              onClick={handleExportClick}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                backgroundColor: '#536DFE',
                '&:hover': { backgroundColor: '#3F51B5' }
              }}
            >
              Export Data
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={projectOptions}
                value={selectedProject}
                onChange={(event, newValue) => {
                  setSelectedProject(newValue);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setProjectSearchInput(newInputValue);
                    if (projectSearchTermRef.current !== newInputValue) {
                      setProjectPage(1);
                      setProjectHasMore(true);
                      projectSearchTermRef.current = newInputValue;
                    }
                  }
                }}
                onOpen={() => {
                  if (projectOptions.length === 0 && !projectFetchingRef.current) {
                    fetchProjects('');
                  }
                }}
                loading={loadingProjects}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Project"
                    placeholder="Search project by name..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProjects ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollBottom = listboxNode.scrollTop + listboxNode.clientHeight;
                    const scrollHeight = listboxNode.scrollHeight;
                    
                    if (scrollHeight > listboxNode.clientHeight && scrollBottom >= scrollHeight - 50) {
                      if (projectHasMore && !loadingProjects && !projectFetchingRef.current) {
                        const nextPage = projectPage + 1;
                        setProjectPage(nextPage);
                        fetchProjects(projectSearchInput || '', nextPage, true);
                      }
                    }
                  },
                  style: { maxHeight: '300px', overflow: 'auto' },
                }}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (option.projectNo && String(option.projectNo).toLowerCase().includes(inputValue.toLowerCase()))
                  );
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="primary"
                startIcon={loadingProjectReport ? <CircularProgress size={20} color="inherit" /> : <Search />}
                onClick={() => {
                  setPage(0);
                  fetchProjectReportData(1, rowsPerPage);
                }}
                disabled={loadingProjectReport}
                fullWidth
                sx={{ height: '40px', textTransform: 'none', fontWeight: 600 }}
              >
                {loadingProjectReport ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={12}>
              {selectedProject && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`Selected: ${selectedProject.name} (${selectedProject.projectNo})`}
                    onDelete={() => {
                      setSelectedProject(null);
                      setPage(0);
                      fetchProjectReportData(1, rowsPerPage);
                    }}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
              {(fromDate || toDate) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  {fromDate && (
                    <Chip
                      label={`From: ${new Date(fromDate).toLocaleDateString()}`}
                      onDelete={() => {
                        setFromDate('');
                        setPage(0);
                        fetchProjectReportData(1, rowsPerPage);
                      }}
                      color="secondary"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {toDate && (
                    <Chip
                      label={`To: ${new Date(toDate).toLocaleDateString()}`}
                      onDelete={() => {
                        setToDate('');
                        setPage(0);
                        fetchProjectReportData(1, rowsPerPage);
                      }}
                      color="secondary"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <Business sx={{ fontSize: 40, color: '#536DFE', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {totalProjects}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Projects
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <TrendingUp sx={{ fontSize: 40, color: '#4caf50', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                Rs. {formatCurrency(totalBookingAmount)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Booking Amount
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CheckCircle sx={{ fontSize: 40, color: '#ff9800', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                Rs. {formatCurrency(totalPaidAmount)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Paid Amount
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <Schedule sx={{ fontSize: 40, color: '#f44336', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                Rs. {formatCurrency(totalDueAmount)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Due Amount
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Data Table */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Project Report Details
          </Typography>
          <TableContainer>
            {loadingProjectReport ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#536DFE' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Project No</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Project Name</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Project Type</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Total Inventory</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Booked</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Available</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Total Bookings</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Booking Amount (Rs.)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Paid Amount (Rs.)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Due Amount (Rs.)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Cancelled</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectReportData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No project data found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    projectReportData.map((row, index) => (
                      <TableRow
                        key={row.projectId || index}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                          '&:hover': { backgroundColor: '#e3f2fd' }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{row.sno}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.projectNo}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.projectName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={row.projectType} 
                            size="small" 
                            color="primary"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(row.totalInventory)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace', color: '#4caf50', fontWeight: 600 }}>{formatNumber(row.bookedInventory)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace', color: '#2196f3', fontWeight: 600 }}>{formatNumber(row.availableInventory)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(row.totalBookings)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>Rs. {formatCurrency(row.totalBookingAmount)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace', color: '#4caf50', fontWeight: 600 }}>Rs. {formatCurrency(row.totalPaidAmount)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace', color: row.totalDueAmount < 0 ? '#f44336' : '#ff9800', fontWeight: 600 }}>Rs. {formatCurrency(row.totalDueAmount)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace', color: '#f44336', fontWeight: 600 }}>{formatNumber(row.cancelledBookings)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <TablePagination
            
            component="div"
            count={totalProjects}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                backgroundColor: '#f8f9fa',
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Export Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => handleExport('pdf')}>
          <ListItemIcon>
            <PictureAsPdf fontSize="small" />
          </ListItemIcon>
          Export as PDF
        </MenuItem>
        <MenuItem onClick={() => handleExport('csv')}>
          <ListItemIcon>
            <Description fontSize="small" />
          </ListItemIcon>
          Export as CSV
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProjectReport;

